import React, { useState, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import TextField from 'domains/core/components/TextField';
import { styles } from '../styles';
import EmojiPicker from './EmojiPicker';
import MergeTagPicker from './MergeTagPicker';

type Props = {
    content: string;
    setContent: (content: string) => void;
};

const SmsEditor = forwardRef<{ insertAtCursor: (text: string) => void }, Props>(({ content, setContent }, ref) => {
    // Cursor position management
    const [cursorPosition, setCursorPosition] = useState({ start: 0, end: 0 });
    const textFieldRef = useRef<HTMLDivElement>(null);

    // Insert text at the current cursor position
    const insertAtCursor = useCallback(
        (insertText: string) => {
            const newContent =
                content.substring(0, cursorPosition.start) + insertText + content.substring(cursorPosition.end);

            setContent(newContent);
        },
        [content, cursorPosition, setContent]
    );

    // Expose methods to parent components
    useImperativeHandle(ref, () => ({
        insertAtCursor,
        getCursorPosition: () => cursorPosition.start,
    }));

    // Event handlers
    const handleEmojiSelect = (emoji: string) => insertAtCursor(emoji);
    const handleMergeTagSelect = (value: string) => insertAtCursor(value);

    return (
        <Box maxWidth="550px">
            <Typography variant="h6" mb={1}>
                Message
            </Typography>
            <Typography variant="body1">
                Write a message that fits your goal. Keep it clear, focused, and ready to send as a text message. Feel
                free to add emojis to make it more engaging!
            </Typography>
            <Box mt={2}>
                <TextField
                    ref={textFieldRef}
                    counterCurrent={content?.length}
                    counterMax={1600}
                    data-testid="content-field"
                    fullWidth
                    inputProps={{
                        maxLength: 1600,
                        'data-testid': 'content-field',
                    }}
                    placeholder="Type your message here"
                    multiline
                    minRows={10}
                    sx={styles.textFieldInput}
                    value={content ?? ''}
                    withCounter
                    onBlur={(e) => {
                        setCursorPosition({ start: e.target.selectionStart || 0, end: e.target.selectionEnd || 0 });
                    }}
                    onChange={(e) => setContent(e.target.value)}
                />
                <Stack direction="row">
                    <EmojiPicker handleSelect={handleEmojiSelect} />
                    <MergeTagPicker handleSelect={handleMergeTagSelect} />
                </Stack>
            </Box>
        </Box>
    );
});

export default React.memo(SmsEditor);
